import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Search } from 'lucide-react';
import { PAYSLIP_TYPES, type DocumentCategory, type PayslipType } from '@timesheet/schemas';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BottomSheet } from '../shared/bottom-sheet';
import { ApprovalScopeToggle } from '../approvals/ApprovalScopeToggle';
import type { ApprovalScope } from '../../hooks/use-approval-scope';
import { monthNames } from './documents.utils';

export interface DocumentFiltersState {
  search: string;
  year?: number;
  month?: number;
  payslipType?: PayslipType;
  userId?: number;
}

export interface PersonOption {
  id: number;
  name: string;
  isActive?: boolean;
}

interface DocumentFiltersProps {
  category: DocumentCategory;
  filters: DocumentFiltersState;
  onChange: (next: DocumentFiltersState) => void;
  years: number[];
  /** People selectable in the person filter; undefined hides the filter. */
  people?: PersonOption[];
  currentUserId?: number;
  /** Only for roles that see every contract (ADMIN, SUPER_ADMIN). */
  scope?: ApprovalScope;
}

const ALL = '__all__';
const ME = '__me__';

/**
 * Search is inline on every tab. The other filters live in a row on desktop
 * and in a bottom sheet (with a badge of active filters) under md.
 */
export const DocumentFilters: React.FC<DocumentFiltersProps> = ({ category, filters, onChange, years, people, currentUserId, scope }) => {
  const { t, i18n } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const isPayslips = category === 'BUSTE_PAGA';
  const isContracts = category === 'CONTRATTI';
  const hasSecondary = isPayslips || isContracts;
  const activeCount = [filters.year, filters.month, filters.payslipType, filters.userId].filter((v) => v !== undefined).length;
  const months = monthNames(i18n.language);

  const numberOr = (value: string) => (value === ALL ? undefined : Number(value));

  const personSelect = people ? (
    <Select
      value={filters.userId === undefined ? ALL : filters.userId === currentUserId ? ME : String(filters.userId)}
      onValueChange={(value) =>
        onChange({ ...filters, userId: value === ALL ? undefined : value === ME ? currentUserId : Number(value) })
      }
    >
      <SelectTrigger className="w-full sm:w-52" aria-label={t('documents.filters.person', 'Persona')}>
        <SelectValue placeholder={t('documents.filters.person', 'Persona')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{t('documents.filters.all', 'Tutti')}</SelectItem>
        {currentUserId !== undefined && people.some((p) => p.id === currentUserId) && (
          <SelectItem value={ME}>{t('documents.filters.me', 'Io')}</SelectItem>
        )}
        {people
          .filter((p) => p.id !== currentUserId)
          .map((p) => (
            <SelectItem key={p.id} value={String(p.id)}>
              {p.name}
              {p.isActive === false ? ` (${t('documents.filters.inactive', 'cessato')})` : ''}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  ) : null;

  const payslipSelects = isPayslips ? (
    <>
      <Select value={filters.year === undefined ? ALL : String(filters.year)} onValueChange={(v) => onChange({ ...filters, year: numberOr(v) })}>
        <SelectTrigger className="w-full sm:w-32" aria-label={t('documents.filters.year', 'Anno')}>
          <SelectValue placeholder={t('documents.filters.year', 'Anno')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('documents.filters.allYears', 'Tutti gli anni')}</SelectItem>
          {years.map((y) => (
            <SelectItem key={y} value={String(y)}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.month === undefined ? ALL : String(filters.month)} onValueChange={(v) => onChange({ ...filters, month: numberOr(v) })}>
        <SelectTrigger className="w-full sm:w-40" aria-label={t('documents.filters.month', 'Mese')}>
          <SelectValue placeholder={t('documents.filters.month', 'Mese')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('documents.filters.allMonths', 'Tutti i mesi')}</SelectItem>
          {months.map((name, index) => (
            <SelectItem key={name} value={String(index + 1)}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={filters.payslipType ?? ALL}
        onValueChange={(v) => onChange({ ...filters, payslipType: v === ALL ? undefined : (v as PayslipType) })}
      >
        <SelectTrigger className="w-full sm:w-44" aria-label={t('documents.filters.type', 'Tipo')}>
          <SelectValue placeholder={t('documents.filters.type', 'Tipo')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('documents.filters.allTypes', 'Tutti i tipi')}</SelectItem>
          {PAYSLIP_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {t(`documents.payslipTypes.${type}`, type)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  ) : null;

  const secondary = (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      {payslipSelects}
      {personSelect}
      {isContracts && scope && scope.directReports.length > 0 && (
        <ApprovalScopeToggle scope={scope} label={t('approvalScope.onlyMineDocuments', 'Solo i miei collaboratori')} />
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
            placeholder={t('documents.filters.search', 'Cerca per titolo o persona')}
            className="pl-9"
            aria-label={t('documents.filters.search', 'Cerca per titolo o persona')}
          />
        </div>
        {hasSecondary && (
          <Button variant="outline" className="relative md:hidden" onClick={() => setSheetOpen(true)} aria-label={t('documents.filters.open', 'Filtri')}>
            <Filter className="h-4 w-4" />
            {activeCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-semibold text-white">
                {activeCount}
              </span>
            )}
          </Button>
        )}
      </div>
      {hasSecondary && <div className="hidden md:block">{secondary}</div>}
      {hasSecondary && (
        <BottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('documents.filters.open', 'Filtri')}>
          <div className="space-y-4">
            {secondary}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onChange({ search: filters.search })}
              >
                {t('documents.filters.reset', 'Azzera')}
              </Button>
              <Button className="flex-1 bg-orange-500 hover:bg-orange-600" onClick={() => setSheetOpen(false)}>
                {t('documents.filters.apply', 'Applica')}
              </Button>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
};
